import { useState } from "react";
import {
  AlertTriangle,
  Building2,
  Calendar,
  CheckSquare,
  LineChart,
  Package,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import HeroStatusChip from "@/components/HeroStatusChip";
import InfrastructureSchoolsModal from "@/components/InfrastructureSchoolsModal";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const getPriority = (health, backlog) => {
  if (backlog >= 6 || health < 94) return { label: "Repair focus", tone: "danger" };
  if (backlog >= 2.5 || health < 98) return { label: "Monitor", tone: "warn" };
  return { label: "Maintain", tone: "" };
};

const getBacklogTag = (backlog) => {
  if (backlog >= 6) return "danger";
  if (backlog >= 2.5) return "warn";
  return "";
};

const buildDonutGradient = (items) => {
  const total = items.reduce((sum, item) => sum + (item.count || 0), 0);
  if (!total) return "conic-gradient(#e2e8f0 0 100%)";

  let cursor = 0;
  const stops = items.map((item) => {
    const share = ((item.count || 0) / total) * 100;
    const start = cursor;
    cursor += share;
    return `${item.color} ${start}% ${cursor}%`;
  });
  return `conic-gradient(${stops.join(", ")})`;
};

const buildGaugeStyle = (value, color = "#10b981") => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, rgba(16,185,129,.13) ${Math.min(value, 100)}% 100%)`,
});

const buildRingStyle = (value, color = "#10b981") => ({
  background: `radial-gradient(circle at center, white 0 56%, transparent 57%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, rgba(16,185,129,.12) ${Math.min(value, 100)}% 100%)`,
});

const INFRA_DRILL_CATEGORIES = {
  minor_repair: { id: "minor_repair", label: "Minor Repair" },
  major_repair: { id: "major_repair", label: "Major Repair" },
  under_construction: { id: "under_construction", label: "Under Construction" },
};

const ClassroomsToiletsInfrastructureTab = ({ overview, blockData, classroomCondition, scopeLabel }) => {
  const [activeCategory, setActiveCategory] = useState(null);

  const openCategory = (key, value) => {
    const count = Number(value) || 0;
    if (count <= 0) return;
    setActiveCategory({ ...INFRA_DRILL_CATEGORIES[key], value: fmt(value) });
  };

  if (!overview) {
    return (
      <div className="ct-empty-card">
        <Building2 className="mx-auto mb-3 opacity-40" size={40} />
        <p>No infrastructure data available for the selected scope.</p>
      </div>
    );
  }

  const healthIndex = overview.classroom_health_index ?? 0;
  const repairBacklogPct = overview.repair_backlog_pct ?? 0;
  const assetReadiness = Math.round((100 - repairBacklogPct) * 10) / 10;
  const infrastructureIndex =
    overview.infrastructure_readiness_index ??
    Math.round(((healthIndex + assetReadiness) / 2) * 10) / 10;
  const totalRepairs = (overview.classrooms_minor_repair ?? 0) + (overview.classrooms_major_repair ?? 0);
  const goodPct =
    overview.total_classrooms > 0
      ? Math.round((overview.classrooms_good / overview.total_classrooms) * 1000) / 10
      : 0;
  const underConstructionPct =
    overview.total_classrooms > 0
      ? Math.min(
          100,
          Math.round((overview.classrooms_under_construction / overview.total_classrooms) * 1000) / 10
        )
      : 0;

  const conditionItems = (classroomCondition?.by_condition ?? []).map((item) => ({
    ...item,
    label: item.condition,
    sub:
      item.condition === "Good"
        ? "Ready for regular use"
        : item.condition === "Major Repair"
          ? "Safety-priority intervention"
          : "Preventive maintenance",
  }));

  const conditionTotal = conditionItems.reduce((sum, item) => sum + (item.count || 0), 0);
  const conditionWithPct = conditionItems.map((item) => ({
    ...item,
    pct: conditionTotal > 0 ? Math.round((item.count / conditionTotal) * 100) : 0,
  }));

  const rankedBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.classroom_health_index ?? 0) - (a.classroom_health_index ?? 0))
    .map((block, index) => ({ ...block, rank: index + 1 }));

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const districtHealthy = healthIndex >= 90;

  return (
    <div className="ct-infra-tab" data-testid="tab-infrastructure-content">
      <section className="ct-hero-card">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-health-main">
              <div className="ct-gauge" style={buildGaugeStyle(healthIndex)} aria-label={`Classroom Health Index ${pct(healthIndex)}`}>
                <div>
                  <strong>{pct(healthIndex)}</strong>
                  <span>
                    Classroom
                    <br />
                    Health
                  </span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>Infrastructure Command Centre</h2>
                <p>
                  Most classrooms are in good condition. The immediate focus is to separate safety-critical
                  major repairs from low-risk minor repairs and monitor under-construction assets to closure.
                </p>
                <div className="ct-status-row">
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant={districtHealthy ? "green" : "amber"}
                    onClick={!districtHealthy ? () => scrollTo("ct-diagnostics") : undefined}
                    ariaLabel={!districtHealthy ? "Needs attention — view infrastructure diagnostics" : undefined}
                  >
                    {districtHealthy ? "Healthy District" : "Needs Attention"}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={
                      totalRepairs > 0
                        ? () =>
                            overview.classrooms_major_repair > 0
                              ? openCategory("major_repair", overview.classrooms_major_repair)
                              : openCategory("minor_repair", overview.classrooms_minor_repair)
                        : undefined
                    }
                    ariaLabel={`${totalRepairs} repair backlog — view schools needing repair`}
                  >
                    {fmt(totalRepairs)} Repair Backlog
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="blue"
                    onClick={() => scrollTo("ct-block-ranking")}
                    ariaLabel={`${overview.total_blocks} blocks covered — view block ranking`}
                  >
                    {fmt(overview.total_blocks)} Blocks Covered
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-icon-box orange">
                <Wrench size={22} />
              </div>
              <div>
                <b>Recommended executive action</b>
                <span>
                  Create a 60-day repair sprint for {fmt(overview.classrooms_major_repair)} major repairs and link
                  under-construction classroom closure to weekly block review.
                </span>
              </div>
            </div>
          </div>
          <div className="ct-metric-grid" aria-label="Infrastructure KPIs">
            <article className="ct-metric-card">
              <small>Total Schools</small>
              <strong>{fmt(overview.total_schools)}</strong>
              <span className="ct-metric-sub">{fmt(overview.total_blocks)} blocks covered</span>
            </article>
            <article className="ct-metric-card">
              <small>Total Classrooms</small>
              <strong>{fmt(overview.total_classrooms)}</strong>
              <span className="ct-metric-sub">{overview.avg_classrooms_per_school} avg/school</span>
            </article>
            <article className="ct-metric-card">
              <small>Good Condition</small>
              <strong>{fmt(overview.classrooms_good)}</strong>
              <span className="ct-metric-sub">{goodPct}% of classrooms</span>
            </article>
            <article
              className={`ct-metric-card warning${overview.classrooms_minor_repair > 0 ? " clickable" : ""}`}
              role={overview.classrooms_minor_repair > 0 ? "button" : undefined}
              tabIndex={overview.classrooms_minor_repair > 0 ? 0 : undefined}
              onClick={() => openCategory("minor_repair", overview.classrooms_minor_repair)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("minor_repair", overview.classrooms_minor_repair);
                }
              }}
              title={overview.classrooms_minor_repair > 0 ? "View schools with minor repair classrooms" : undefined}
            >
              <small>Minor Repair</small>
              <strong>{fmt(overview.classrooms_minor_repair)}</strong>
              <span className="ct-metric-sub">Low-cost backlog</span>
            </article>
            <article
              className={`ct-metric-card danger${overview.classrooms_major_repair > 0 ? " clickable" : ""}`}
              role={overview.classrooms_major_repair > 0 ? "button" : undefined}
              tabIndex={overview.classrooms_major_repair > 0 ? 0 : undefined}
              onClick={() => openCategory("major_repair", overview.classrooms_major_repair)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("major_repair", overview.classrooms_major_repair);
                }
              }}
              title={overview.classrooms_major_repair > 0 ? "View schools with major repair classrooms" : undefined}
            >
              <small>Major Repair</small>
              <strong>{fmt(overview.classrooms_major_repair)}</strong>
              <span className="ct-metric-sub">Safety-priority repairs</span>
            </article>
            <article
              className={`ct-metric-card${overview.classrooms_under_construction > 0 ? " clickable" : ""}`}
              role={overview.classrooms_under_construction > 0 ? "button" : undefined}
              tabIndex={overview.classrooms_under_construction > 0 ? 0 : undefined}
              onClick={() => openCategory("under_construction", overview.classrooms_under_construction)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openCategory("under_construction", overview.classrooms_under_construction);
                }
              }}
              title={
                overview.classrooms_under_construction > 0
                  ? "View schools with classrooms under construction"
                  : undefined
              }
            >
              <small>Under Construction</small>
              <strong>{fmt(overview.classrooms_under_construction)}</strong>
              <span className="ct-metric-sub">Track completion</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ct-section-title" id="ct-domains">
        <div>
          <h2>Infrastructure Health Snapshot</h2>
          <p>
            Key operating indicators are grouped by decision area so officers can see capacity, condition,
            backlog and readiness without scanning dense tables.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-block-ranking")}>
          View block ranking →
        </button>
      </div>

      <section className="ct-domain-grid">
        <article className="ct-domain-card">
          <div className="ct-domain-head">
            <div className="ct-icon-box green">
              <ShieldCheck size={22} />
            </div>
            <span className="ct-status-dot" />
          </div>
          <h3>Classroom Health</h3>
          <div className="ct-domain-score">
            <strong>{healthIndex}</strong>
            <span>Index</span>
          </div>
          <div className="ct-progress-line">
            <i style={{ width: `${Math.min(healthIndex, 100)}%`, background: "var(--ct-success)" }} />
          </div>
          <div className="ct-domain-meta">
            <span>Healthy</span>
            <span>{fmt(overview.classrooms_good)} good</span>
          </div>
        </article>

        <article className="ct-domain-card">
          <div className="ct-domain-head">
            <div className="ct-icon-box">
              <Building2 size={22} />
            </div>
            <span className="ct-status-dot" />
          </div>
          <h3>Asset Readiness</h3>
          <div className="ct-domain-score">
            <strong>{assetReadiness}</strong>
            <span>Index</span>
          </div>
          <div className="ct-progress-line">
            <i style={{ width: `${Math.min(assetReadiness, 100)}%` }} />
          </div>
          <div className="ct-domain-meta">
            <span>Operational</span>
            <span>{overview.avg_classrooms_per_school}/classroom</span>
          </div>
        </article>

        <article className="ct-domain-card">
          <div className="ct-domain-head">
            <div className="ct-icon-box orange">
              <Wrench size={22} />
            </div>
            <span className="ct-status-dot amber" />
          </div>
          <h3>Repair Burden</h3>
          <div className="ct-domain-score">
            <strong>{repairBacklogPct}</strong>
            <span>% backlog</span>
          </div>
          <div className="ct-progress-line">
            <i
              style={{
                width: `${Math.min(repairBacklogPct * 10, 100)}%`,
                background: "var(--ct-warning)",
              }}
            />
          </div>
          <div className="ct-domain-meta">
            <span>{fmt(totalRepairs)} repairs</span>
            <span>{fmt(overview.classrooms_major_repair)} major</span>
          </div>
        </article>

        <article className="ct-domain-card">
          <div className="ct-domain-head">
            <div className="ct-icon-box purple">
              <Package size={22} />
            </div>
            <span className="ct-status-dot amber" />
          </div>
          <h3>Under Construction</h3>
          <div className="ct-domain-score">
            <strong>{fmt(overview.classrooms_under_construction)}</strong>
            <span>rooms</span>
          </div>
          <div className="ct-progress-line">
            <i style={{ width: `${underConstructionPct}%`, background: "var(--ct-purple)" }} />
          </div>
          <div className="ct-domain-meta">
            <span>Track closure</span>
            <span>Weekly review</span>
          </div>
        </article>
      </section>

      <div className="ct-section-title" id="ct-diagnostics">
        <div>
          <h2>Condition Diagnostics</h2>
          <p>
            Charts are given enough space and paired with readable legends so the condition split and health
            indices are easier to understand.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-actions")}>
          Go to action queue →
        </button>
      </div>

      <section className="ct-analytics-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Classroom Condition Distribution</h3>
              <p>Good classrooms dominate, with major and minor repair pockets requiring block-level intervention.</p>
            </div>
            <span className="ct-pill">{fmt(overview.total_classrooms)} rooms</span>
          </div>
          <div className="ct-donut-area">
            <div
              className="ct-large-donut"
              style={{ background: buildDonutGradient(conditionWithPct) }}
              aria-hidden="true"
            />
            <div className="ct-legend-list">
              {conditionWithPct.map((item) => (
                <div className="ct-legend-item" key={item.label}>
                  <span
                    className={`ct-legend-dot ${
                      item.label === "Major Repair" ? "red" : item.label === "Minor Repair" ? "orange" : ""
                    }`}
                  />
                  <div>
                    <b>{item.label === "Good" ? "Good condition" : item.label.toLowerCase()}</b>
                    <div className="ct-legend-sub">{item.sub}</div>
                  </div>
                  <b>{item.pct}%</b>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Classroom Health Metrics</h3>
              <p>Three headline indices converted into clear, comparable score cards.</p>
            </div>
            <span className="ct-pill">Index view</span>
          </div>
          <div className="ct-ring-grid">
            <div className="ct-ring-card">
              <div className="ct-ring" style={buildRingStyle(healthIndex)}>
                <strong>{pct(healthIndex)}</strong>
              </div>
              <b>Classroom Health</b>
              <span>Condition quality</span>
            </div>
            <div className="ct-ring-card">
              <div className="ct-ring" style={buildRingStyle(assetReadiness, "#2563eb")}>
                <strong style={{ color: "#2563eb" }}>{pct(assetReadiness)}</strong>
              </div>
              <b>Asset Readiness</b>
              <span>Usable capacity</span>
            </div>
            <div className="ct-ring-card">
              <div className="ct-ring" style={buildRingStyle(infrastructureIndex, "#14b8a6")}>
                <strong style={{ color: "#14b8a6" }}>{pct(infrastructureIndex)}</strong>
              </div>
              <b>Infrastructure Overview</b>
              <span>Composite status</span>
            </div>
          </div>
        </article>
      </section>

      <div className="ct-section-title" id="ct-actions">
        <div>
          <h2>Repair Prioritisation and Action Queue</h2>
          <p>
            The dashboard moves from raw statistics to operational decisions: where to repair first, what to
            monitor, and what can be closed quickly.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-block-ranking")}>
          See full ranking →
        </button>
      </div>

      <section className="ct-two-column">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Priority Repair Queue</h3>
              <p>Focus on blocks with higher backlog or lower WASH resilience.</p>
            </div>
            <span className="ct-pill">Next 60 days</span>
          </div>
          <div className="ct-repair-list">
            <div className="ct-repair-item">
              <div className="ct-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <h4>Major repair sprint</h4>
                <p>
                  Bundle {fmt(overview.classrooms_major_repair)} major repairs by safety risk and approval
                  readiness.
                </p>
              </div>
              <div className="ct-repair-score">
                High
                <span>Safety</span>
              </div>
            </div>
            <div className="ct-repair-item">
              <div className="ct-icon-box orange">
                <Wrench size={22} />
              </div>
              <div>
                <h4>Preventive maintenance batch</h4>
                <p>
                  Resolve {fmt(overview.classrooms_minor_repair)} minor repairs before they convert into
                  safety-critical cases.
                </p>
              </div>
              <div className="ct-repair-score">
                Medium
                <span>Cost save</span>
              </div>
            </div>
            <div className="ct-repair-item">
              <div className="ct-icon-box purple">
                <Package size={22} />
              </div>
              <div>
                <h4>Under-construction closure</h4>
                <p>
                  Track {fmt(overview.classrooms_under_construction)} classrooms with milestone, contractor and
                  completion-date visibility.
                </p>
              </div>
              <div className="ct-repair-score">
                Weekly
                <span>Review</span>
              </div>
            </div>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Officer Action Cards</h3>
              <p>Simple next steps for block and district teams.</p>
            </div>
            <span className="ct-pill">Actionable</span>
          </div>
          <div className="ct-action-card">
            <div className="ct-action-item">
              <div className="ct-icon-box green">
                <CheckSquare size={22} />
              </div>
              <div>
                <h4>Approve ready repairs first</h4>
                <p>Separate estimate-approved repairs from survey-pending cases to speed up execution.</p>
              </div>
            </div>
            <div className="ct-action-item">
              <div className="ct-icon-box">
                <LineChart size={22} />
              </div>
              <div>
                <h4>Monitor top backlog blocks</h4>
                <p>Review repair backlog, WASH index and classroom health together in weekly reviews.</p>
              </div>
            </div>
            <div className="ct-action-item">
              <div className="ct-icon-box orange">
                <Calendar size={22} />
              </div>
              <div>
                <h4>Set 30/60/90-day targets</h4>
                <p>Major repairs in 60 days, minor repairs in 90 days, and construction status every week.</p>
              </div>
            </div>
          </div>
        </article>
      </section>

      <section className="ct-panel ct-table-card" id="ct-block-ranking">
        <div className="ct-table-head">
          <div>
            <h3>Block-Wise Infrastructure Performance</h3>
            <p>Ranking combines classroom health, repair backlog and WASH index for operational prioritisation.</p>
          </div>
          <span className="ct-pill">Top blocks</span>
        </div>
        <table className="ct-infra-table" aria-label="Block-wise infrastructure performance">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Block</th>
              <th>Schools</th>
              <th>Classrooms</th>
              <th>Avg/School</th>
              <th>Health Index</th>
              <th>Repair Backlog</th>
              <th>WASH Index</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {rankedBlocks.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", color: "var(--ct-muted)", fontWeight: 600 }}>
                  No block ranking data available for the selected scope.
                </td>
              </tr>
            ) : (
              rankedBlocks.map((block) => {
                const priority = getPriority(block.classroom_health_index ?? 0, block.repair_backlog_pct ?? 0);
                const backlogTone = getBacklogTag(block.repair_backlog_pct ?? 0);
                return (
                  <tr key={block.block_name}>
                    <td>{block.rank}</td>
                    <td>{block.block_name}</td>
                    <td>{fmt(block.total_schools)}</td>
                    <td>{fmt(block.total_classrooms)}</td>
                    <td>{block.avg_classrooms}</td>
                    <td className="ct-score">{pct(block.classroom_health_index)}</td>
                    <td>
                      <span className={`ct-tag ${backlogTone}`}>{pct(block.repair_backlog_pct)}</span>
                    </td>
                    <td>
                      <span className="ct-tag">{pct(block.wash_index)}</span>
                    </td>
                    <td>
                      <span className={`ct-tag ${priority.tone}`}>{priority.label}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      <InfrastructureSchoolsModal
        open={Boolean(activeCategory)}
        category={activeCategory}
        onClose={() => setActiveCategory(null)}
        scopeLabel={scopeLabel}
      />
    </div>
  );
};

export default ClassroomsToiletsInfrastructureTab;
